import type { AppRouter } from '@app/api'
import {
  type CalendarSpelling,
  type PublicLanguage,
  type PublicScript,
  type Said,
  say,
  spelled,
} from '@app/shared'
import type { inferRouterOutputs } from '@trpc/server'

type Lead = NonNullable<
  inferRouterOutputs<AppRouter>['occasion']['leading']['lead']
>

/**
 * The words a shul's home page writes itself around what leads, as questions
 * with one answer.
 *
 * Every Shabbos-family word here is the app's own, authored in the Ashkenazi
 * spelling and carried into the shul's by `spelled`. A name the calendar
 * supplied — a special Shabbos's, the parsha's — is already in the shul's
 * spelling and is never passed through it, and neither is the shul's own label.
 */

/**
 * A weekly Shabbos's English name: its special name where the calendar gives
 * one, whole ("Shabbos Shekalim"), else its parsha.
 */
export function shabbosName(lead: Lead, spelling: CalendarSpelling): string {
  const special = lead.specialShabbos[0]?.en
  if (special) return special
  return lead.parsha?.en
    ? `${spelled('Shabbos Parshas', spelling)} ${lead.parsha.en}`
    : spelled('Shabbos', spelling)
}

/** A weekly Shabbos's Hebrew name: its special name and its parsha. */
export const shabbosNameHe = (lead: Lead): string | null =>
  [lead.specialShabbos[0]?.he, lead.parsha?.he].filter(Boolean).join(' · ') ||
  null

/** The occurrence's two names, as the page heads it. */
export const namesOf = (lead: Lead, spelling: CalendarSpelling) =>
  lead.cadence === 'weekly'
    ? { en: shabbosName(lead, spelling), he: shabbosNameHe(lead) }
    : { en: lead.labelEn, he: lead.labelHe }

/**
 * The parsha under a special Shabbos's English name — "Parshas Vayakhel"
 * under "Shabbos Shekalim" — and nothing where the name already is the parsha.
 */
export function parshaBeneath(
  lead: Lead,
  spelling: CalendarSpelling,
): string | null {
  return lead.cadence === 'weekly' &&
    lead.specialShabbos[0]?.en &&
    lead.parsha?.en
    ? `${spelled('Parshas', spelling)} ${lead.parsha.en}`
    : null
}

/**
 * Facts about this occurrence worth a line — and none when none is true.
 * Shabbos Mevorchim is named as occasions are, in the words the printed sheet
 * announces it with; a line printing only this week is named as lines are.
 */
export function factsOf(
  lead: Lead,
  language: Pick<PublicLanguage, 'occasions' | 'lines'>,
  spelling: CalendarSpelling,
): Said[] {
  if (lead.state === 'during') return []
  return [
    ...(lead.mevorchim
      ? [
          say(
            language.occasions,
            spelled('Shabbos Mevorchim', spelling),
            'שבת מברכים',
          ),
        ]
      : []),
    ...lead.alsoThisWeek.map((l) => say(language.lines, l.labelEn, l.labelHe)),
  ]
}

/**
 * A way to a yearly occasion's schedule. An action is a sentence in English,
 * so an occasion the site names in Hebrew alone is left out of it rather than
 * set inside it.
 */
export function scheduleLink(
  occasion: { labelEn: string; labelHe: string },
  script: PublicScript,
): string {
  const name = say(script, occasion.labelEn, occasion.labelHe).en
  return name ? `See the ${name} schedule` : 'See the schedule'
}

/** The hero's way to the schedule. */
export function scheduleLabel(
  lead: Lead,
  script: PublicScript,
  spelling: CalendarSpelling,
): string {
  return lead.cadence === 'weekly'
    ? spelled("See this Shabbos's times", spelling)
    : scheduleLink(lead, script)
}
